import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import useDecks from "../../hooks/useDecks";
import { decksApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import PageHeader from "../PageHeader";
import { DELETE_DISARM_MS, deckCounts } from "./deckText";

/**
 * Study → Decks (board #23): make decks, see how big each is and how much of
 * it is due, jump straight into reviewing one. A deck is only a label on
 * cards — deleting one never deletes a card.
 */
function DeckList() {
  const { decks, loading, error, refreshDecks } = useDecks();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [editing, setEditing] = useState(null); // {id, name, description}
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [armedId, setArmedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [notice, setNotice] = useState(null);
  const disarmTimer = useRef(null);

  useEffect(() => () => clearTimeout(disarmTimer.current), []);
  // Counts change elsewhere (a card created in Create, moved in Edit,
  // reviewed): refresh whenever this page opens.
  useEffect(() => {
    refreshDecks();
  }, [refreshDecks]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    setCreateError(null);
    try {
      await decksApi.create({ name: trimmed, description: description.trim() });
      setName("");
      setDescription("");
      setNotice(
        `Created “${trimmed}”. Add cards to it from Create or from a card's Edit.`,
      );
      await refreshDecks();
    } catch (err) {
      setCreateError(apiErrorMessage(err, "Couldn't create the deck."));
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const trimmed = editing.name.trim();
    if (!trimmed) return;
    setSaving(true);
    setEditError(null);
    try {
      await decksApi.update(editing.id, {
        name: trimmed,
        description: editing.description.trim(),
      });
      setEditing(null);
      await refreshDecks();
    } catch (err) {
      setEditError(apiErrorMessage(err, "Couldn't save the deck."));
    } finally {
      setSaving(false);
    }
  };

  const arm = (id) => {
    setDeleteError(null);
    setArmedId(id);
    clearTimeout(disarmTimer.current);
    disarmTimer.current = setTimeout(() => setArmedId(null), DELETE_DISARM_MS);
  };

  const handleDelete = async (deck) => {
    clearTimeout(disarmTimer.current);
    setDeletingId(deck.id);
    setDeleteError(null);
    try {
      await decksApi.delete(deck.id);
      setArmedId(null);
      setNotice(
        deck.card_count > 0
          ? `Deleted “${deck.name}”. Its ${deck.card_count === 1 ? "card is" : "cards are"} still in your library.`
          : `Deleted “${deck.name}”.`,
      );
      await refreshDecks();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, "Couldn't delete the deck."));
      // Still armed after a failure — let it disarm itself as usual.
      disarmTimer.current = setTimeout(
        () => setArmedId(null),
        DELETE_DISARM_MS,
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Decks"
        subtitle="Group cards to review one topic at a time. A deck is just a label: deleting one keeps its cards, and a card can be in several decks."
      />

      <form
        onSubmit={handleCreate}
        className="mb-6 grid max-w-xl gap-3 rounded-lg border bg-card p-4"
        aria-label="Make a deck"
      >
        <div className="grid gap-1.5">
          <Label htmlFor="deck-name">New deck</Label>
          <Input
            id="deck-name"
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Spanish verbs"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="deck-description">
            Description{" "}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </Label>
          <Textarea
            id="deck-description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What goes in this deck"
          />
        </div>
        {createError && (
          <p role="alert" className="text-sm text-destructive">
            {createError}
          </p>
        )}
        <div>
          <Button type="submit" disabled={!name.trim() || creating}>
            {creating ? "Creating…" : "Create deck"}
          </Button>
        </div>
      </form>

      {notice && (
        <Alert className="mb-3 py-2" role="status">
          <AlertDescription className="text-sm">{notice}</AlertDescription>
        </Alert>
      )}
      {deleteError && (
        <Alert variant="destructive" className="mb-3 py-2">
          <AlertDescription className="text-sm">{deleteError}</AlertDescription>
        </Alert>
      )}

      {loading && decks.length === 0 && (
        <div
          className="flex items-center gap-2 text-sm text-muted-foreground"
          role="status"
        >
          <Loader2 className="size-4 animate-spin" />
          Loading your decks…
        </div>
      )}
      {error && !loading && (
        <Alert className="py-2">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1 text-sm">
              Couldn&apos;t load your decks.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={refreshDecks}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {!loading && !error && decks.length === 0 && (
        <p className="text-sm text-muted-foreground italic">
          No decks yet — make one above, then add cards to it when you create or
          edit them.
        </p>
      )}

      {decks.length > 0 && (
        <ul
          className="list-none divide-y rounded-md border p-0"
          aria-label="Your decks"
        >
          {decks.map((deck) => (
            <li
              key={deck.id}
              className="flex flex-wrap items-center gap-3 px-3 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">{deck.name}</div>
                {deck.description && (
                  <div className="text-sm text-muted-foreground">
                    {deck.description}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  {deckCounts(deck)}
                </div>
              </div>
              {deck.due_count > 0 ? (
                <Button asChild size="sm">
                  <Link to={`/study/review?deck=${deck.id}`}>
                    Review {deck.due_count}
                  </Link>
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Nothing due
                </span>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={`Edit ${deck.name}`}
                onClick={() => {
                  setEditError(null);
                  setEditing({
                    id: deck.id,
                    name: deck.name,
                    description: deck.description || "",
                  });
                }}
              >
                Edit
              </Button>
              {armedId === deck.id ? (
                <span className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">
                    Delete it? Cards are kept.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    disabled={deletingId === deck.id}
                    onClick={() => handleDelete(deck)}
                  >
                    {deletingId === deck.id ? "Deleting…" : "Delete"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={deletingId === deck.id}
                    onClick={() => setArmedId(null)}
                  >
                    Keep
                  </Button>
                </span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Delete ${deck.name}`}
                  // One delete at a time: a second one would share the
                  // in-flight state and be disarmed by the first's success.
                  disabled={deletingId !== null}
                  onClick={() => arm(deck.id)}
                >
                  Delete
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && !saving && setEditing(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          // Static backdrop, like the app's other editing dialogs: a stray
          // click outside mustn't throw away a typed rename.
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          {editing && (
            <form onSubmit={handleSave} className="grid gap-4">
              <DialogHeader>
                <DialogTitle>Edit deck</DialogTitle>
                <DialogDescription>
                  Renaming keeps every card in the deck.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-1.5">
                <Label htmlFor="edit-deck-name">Name</Label>
                <Input
                  id="edit-deck-name"
                  value={editing.name}
                  maxLength={255}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="edit-deck-description">Description</Label>
                <Textarea
                  id="edit-deck-description"
                  rows={2}
                  value={editing.description}
                  onChange={(e) =>
                    setEditing({ ...editing, description: e.target.value })
                  }
                />
              </div>
              {editError && (
                <p role="alert" className="text-sm text-destructive">
                  {editError}
                </p>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={saving}
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!editing.name.trim() || saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DeckList;
